import { useEffect, useId, useState, type ReactNode } from 'react'

import { useI18n } from '@runtime/core/i18n/i18n'
import CharacterHelpModal from '@/features/help/components/CharacterHelpModal'
import { Tabs, TabButton } from '@runtime/components/ui/Tabs'
import styles from '@/features/help/components/TabbedCharacterHelpModal.module.scss'

type HelpTab = {
  id: string
  label: string
  content: ReactNode
}

type TabbedCharacterHelpModalProps = {
  open: boolean
  character: string
  title?: string
  tabs: HelpTab[]
  onClose: () => void
  defaultTabId?: string
  white?: boolean
}

export default function TabbedCharacterHelpModal({
  open,
  character,
  title,
  tabs,
  onClose,
  defaultTabId,
  white,
}: TabbedCharacterHelpModalProps) {
  const { t } = useI18n()
  const tabPrefix = useId()
  const firstTabId = tabs && tabs.length ? tabs[0].id : null
  const resolvedDefault =
    defaultTabId && tabs.some(tab => tab.id === defaultTabId) ? defaultTabId : firstTabId
  const [activeTab, setActiveTab] = useState(resolvedDefault)

  useEffect(() => {
    if (!open) return
    setActiveTab(resolvedDefault)
  }, [open, resolvedDefault])

  useEffect(() => {
    if (!open || !activeTab) return
    const id = `${tabPrefix}-tab-${activeTab}`
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null
    if (el && typeof (el as HTMLButtonElement).focus === 'function') {
      ;(el as HTMLButtonElement).focus()
    }
  }, [open, activeTab, tabPrefix])

  const currentTab = tabs.find(tab => tab.id === activeTab) || (tabs.length ? tabs[0] : undefined)

  return (
    <CharacterHelpModal
      open={open}
      character={character}
      title={title}
      preAvatar={
        <div className={styles.tabsWrap}>
          <Tabs
            variant="strategy"
            className={styles.helpTabs}
            role="tablist"
            aria-label={title || t('common.help')}
          >
            {tabs.map(tab => {
              const tabId = `${tabPrefix}-tab-${tab.id}`
              const panelId = `${tabPrefix}-panel-${tab.id}`
              const isActive = tab.id === currentTab?.id
              return (
                <TabButton
                  key={tab.id}
                  active={isActive}
                  className={`${styles.helpTab} ${isActive ? styles.helpTabActive : ''}`}
                  role="tab"
                  id={tabId}
                  aria-selected={isActive}
                  aria-controls={panelId}
                  aria-label={tab.label}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </TabButton>
              )
            })}
          </Tabs>
        </div>
      }
      onClose={onClose}
      white={white}
      noTextWrap
    >
      {currentTab ? (
        <section
          id={`${tabPrefix}-panel-${currentTab.id}`}
          role="tabpanel"
          aria-labelledby={`${tabPrefix}-tab-${currentTab.id}`}
          className={styles.tabPanel}
          tabIndex={0}
        >
          <div className={styles.tabContent}>{currentTab.content}</div>
        </section>
      ) : null}
    </CharacterHelpModal>
  )
}
